import React, { Component } from 'react';
import styled from 'styled-components';
import { PropTypes } from 'prop-types';

import { Button, Notice } from '@appcues/component-library';
import BaseModal from 'components/ModalContainer/BaseModal';
import ManageTagOption from 'components/ModalContainer/TagManagementModal/ManageTagOption';
import SearchBar from 'components/Common/SearchBar';
import CreateTagOption from 'components/ModalContainer/TagManagementModal/CreateTagOption';
import { isAlphaNumericString } from 'utils/validation';
import { getTagsAsList, isTagNameUnique } from 'helpers/tags';
import { getStepsWithTag } from 'helpers/steps';
import { CONFIRM_GLOBAL_TAG_DELETE_MODAL } from 'constants/globals';
import Loader from 'components/Common/Loader';

const getFilteredTags = (tags, searchValue) => {
  if (!searchValue) {
    return tags;
  }

  const normalizedSearchValue = searchValue.toLowerCase().trim();

  return tags.filter(tag => {
    const normalizedTagName = tag.name.toLowerCase().trim();
    return normalizedTagName.includes(normalizedSearchValue);
  });
};

class TagManagementModal extends Component {
  state = {
    isCreatingTag: false,
    tagSearchValue: '',
    isEditingTagId: null,
  };

  componentWillUnmount() {
    this.resetCreateTagResult();
  }

  setTagSearchValue(searchValue) {
    this.resetCreateTagResult();
    this.setState({
      tagSearchValue: searchValue,
    });
  }

  hideModal = () => {
    const { actions } = this.props;
    actions.hideModal();
  };

  toggleCreatingTag = () => {
    this.setState(({ isCreatingTag }) => ({
      isCreatingTag: !isCreatingTag,
    }));
  };

  startCreatingTag = () => {
    this.resetCreateTagResult();
    this.setEditingTagId(null);
    this.toggleCreatingTag();
  };

  stopCreatingTag = () => {
    this.resetCreateTagResult();
    this.toggleCreatingTag();
  };

  handleCreateTag = tagName => {
    this.resetCreateTagResult();
    const {
      tags,
      actions: { createTag, updateTagsManagementState },
    } = this.props;

    const trimmed = tagName.trim();

    if (!isAlphaNumericString(trimmed)) {
      updateTagsManagementState(
        `Sorry, that's not a valid tag name. Try adding an alphanumeric tag name.`,
        null
      );
      return;
    }
    if (!isTagNameUnique(trimmed, tags)) {
      updateTagsManagementState(`A tag by that name already exists.`, null);
      return;
    }
    createTag(trimmed);

    this.toggleCreatingTag();
    updateTagsManagementState(null, `Added "${trimmed}" to your tags.`);
  };

  handleDeleteTag = tag => {
    const { actions, steps } = this.props;
    const stepsList = Object.values(steps);

    const flowsWithTag = getStepsWithTag([...stepsList], tag.id);
    const numFlowsWithTag = flowsWithTag.length;
    const isTagUsedOnFlows = numFlowsWithTag > 0;

    if (isTagUsedOnFlows) {
      // When deleting a tag that is being used, we'll show
      // a confirmation modal to indicate the number of affected
      // flows. Separately, a cloud function will remove the tag
      // off those flows once the delete is confirmed.
      actions.showModal(CONFIRM_GLOBAL_TAG_DELETE_MODAL, {
        tag,
        numOfMatchingSteps: numFlowsWithTag,
      });
    } else {
      this.deleteTagGlobally(tag);
    }
  };

  deleteTagGlobally = tag => {
    const {
      actions: { deleteTag },
    } = this.props;
    // When a tag is globally deleted and it is used as a flow
    // list filter, a cloud function will remove that tag from
    // the filter.
    this.resetCreateTagResult();
    deleteTag(tag.id);
  };

  handleEditTag = (tagId, newTagName) => {
    this.resetCreateTagResult();
    const {
      tags,
      actions: { editTag, updateTagsManagementState },
    } = this.props;
    const isTagNameSame =
      (tags[tagId] &&
        tags[tagId].name &&
        tags[tagId].name.toLowerCase().trim()) ===
      newTagName.toLowerCase().trim();
    if (isTagNameSame) return;
    if (!isAlphaNumericString(newTagName)) {
      updateTagsManagementState(
        `Sorry, that's not a valid tag name. Please use an alphanumeric tag name.`,
        null
      );
      return;
    }
    if (!isTagNameUnique(newTagName, tags)) {
      updateTagsManagementState(`A tag by that name already exists.`, null);
      return;
    }
    editTag(tagId, newTagName);
    updateTagsManagementState(null, `Changed tag name to "${newTagName}".`);
  };

  handleStartEditingTag = tagId => {
    const { isCreatingTag } = this.state;
    if (isCreatingTag) {
      this.toggleCreatingTag();
    }
    this.setEditingTagId(tagId);
  };

  resetCreateTagResult = () => {
    const { actions } = this.props;
    actions.updateTagsManagementState(null, null);
  };

  setEditingTagId = tagId => {
    this.setState({
      isEditingTagId: tagId,
    });
  };

  render() {
    const { tagSearchValue, isCreatingTag, isEditingTagId } = this.state;
    const { tags, tagsAreSynced, tagsManagementState } = this.props;
    const hasTags = Object.keys(tags).length > 0;
    const tagsList = getTagsAsList(tags);
    const filteredTags = getFilteredTags(tagsList, tagSearchValue);

    return (
      <BaseModal {...this.props}>
        <div className="modalHeader">
          <span className="modalHeaderTitle">Tags</span>
          <Button className="button-success" onClick={this.startCreatingTag}>
            Create Tag
          </Button>
        </div>
        {tagsManagementState.errorMessage && (
          <Notice type="error">{tagsManagementState.errorMessage}</Notice>
        )}
        {tagsManagementState.successMessage && (
          <Notice type="success">{tagsManagementState.successMessage}</Notice>
        )}

        {hasTags && (
          <SearchBar
            className="modalRow"
            placeholder="Search Tags"
            value={tagSearchValue}
            handleNewSearchValue={searchValue =>
              this.setTagSearchValue(searchValue)
            }
          />
        )}
        <div className="tags">
          {!tagsAreSynced && (
            <div className="loaderContainer">
              <Loader margin="0px 20px" />
            </div>
          )}
          <div className="tagsContainer">
            {isCreatingTag && (
              <CreateTagOption
                className="tagOption"
                handleCreateTag={this.handleCreateTag}
                handleCancelCreatingTag={this.stopCreatingTag}
              />
            )}
            {tagsAreSynced &&
              filteredTags.map(tag => {
                return (
                  <ManageTagOption
                    key={tag.id}
                    className="tagOption"
                    tag={tag}
                    isEditingTag={isEditingTagId === tag.id}
                    handleStartEditingTag={this.handleStartEditingTag}
                    handleDeleteTag={this.handleDeleteTag}
                    handleEditTag={this.handleEditTag}
                  />
                );
              })}
            {!hasTags && <span>No tags were found.</span>}
          </div>
        </div>
        <div className="modalRow buttonOptions">
          <Button className="button-primary" onClick={this.hideModal}>
            Done
          </Button>
        </div>
      </BaseModal>
    );
  }
}

TagManagementModal.propTypes = {
  tags: PropTypes.object.isRequired,
};

export default styled(TagManagementModal)`
  .modalHeader {
    display: flex;
    justify-content: space-between;
    margin: 10px 0px 20px 0px;

    .modalHeaderTitle {
      font-size: 24px;
    }
  }

  .modalRow {
    margin-bottom: 25px;

    &.buttonOptions {
      margin-bottom: 0;
    }
  }

  .tags {
    margin-bottom: 15px;
  }

  .buttonOptions {
    display: flex;
    justify-content: flex-end;
  }

  .tagOption {
    margin-bottom: 16px;
    font-size: 12px;
  }

  .loaderContainer {
    width: 100%;
    height: 20px;
  }

  .tagsContainer {
    overflow-y: auto;
    padding: 5px;
    min-height: 150px;
    height: 250px;
  }

  input {
    font-size: 12px;
  }
`;
